import { useEffect, useRef, useState } from "react";
import type { EnvelopeFromServer, StepEnvelope, TritonMode } from "../lib/aide-protocol";
import { tritonPrefillFromCard, useTritonHandoff } from "../lib/triton-handoff";

// One chat mode end-to-end: find → negotiate → book. Post-booking
// modifications live on the /manage tab, not behind a mode toggle.
// We keep the TritonMode prop for backward-compat with envelopes but
// the UI no longer surfaces the mode picker.
const TRITON_DEFAULT_MODE: TritonMode = "explore";
const TRITON_PLACEHOLDER =
  "Find something fun this weekend, book kaiseki under ¥25k Friday, get me a ryokan with onsen for two…";
const TRITON_QUICK_CHIPS: string[] = [
  "find something fun this weekend",
  "book kaiseki for 2 friday under ¥25,000",
  "get me a ryokan with onsen for two nights",
];

// Triton chat per LS-SPEC-22 §Wire Protocol + LS-SPEC-32 §Voice.
// Strictly a render surface — every message in the list is produced by
// Aide; the composer only emits envelopes. Concierge-warm tone per
// SPEC-32: short, calm, never urgent, no exclamation marks.

interface TritonChatProps {
  status: import("../lib/aide-protocol").ConnectionStatus;
  send: (envelope: import("../lib/aide-protocol").EnvelopeFromClient) => void;
  envelopes: EnvelopeFromServer[];
  pendingAttachmentCount: number;
}

interface UserBubble {
  kind: "user";
  id: string;
  text: string;
  imageRefs: string[];
}

interface AssistantBubble {
  kind: "assistant";
  id: string;
  text: string;
  status: string;
  steps: StepEnvelope[];
}

interface ApprovalBubble {
  kind: "approval";
  id: string;
  planId: string;
  summaryText: string;
  classification: string;
  requiredTier: string;
  permittedTier: string;
  steps: Array<{ tool: string; rationale: string }>;
}

type Bubble = UserBubble | AssistantBubble | ApprovalBubble;


export function TritonChat({ status, send, envelopes, pendingAttachmentCount }: TritonChatProps) {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [composer, setComposer] = useState("");
  // One unified chat mode — find / negotiate / book all happen here.
  // The old explore/negotiate/manage toggle was UX friction; the
  // keyword classifier already routes "book me X" / "find a deal"
  // through the scout/PACT mission path regardless of mode.
  const mode: TritonMode = TRITON_DEFAULT_MODE;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const consumePendingCard = useTritonHandoff((s) => s.consumePendingCard);

  // If the consumer tapped "Ask Triton" on an Alexandria card, pre-fill
  // the composer with a card-grounded prompt + focus it. We consume the
  // pending card so navigating away + back doesn't repeat the prefill.
  useEffect(() => {
    const card = consumePendingCard();
    if (card) {
      setComposer(tritonPrefillFromCard(card));
      inputRef.current?.focus();
    }
    // intentional: run only on mount; consumePendingCard is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derive new bubbles from server envelopes (assistant messages /
  // approval requests). The user's own bubbles are appended at send
  // time so we don't echo through the server.
  useEffect(() => {
    const last = envelopes[envelopes.length - 1];
    if (!last) return;
    if (last.type === "assistant_message") {
      setBubbles((prev) => [
        ...prev,
        {
          kind: "assistant",
          id: last.plan_id,
          text: last.text || "(no summary)",
          status: last.status,
          steps: last.steps,
        },
      ]);
    } else if (last.type === "request_approval") {
      setBubbles((prev) => [
        ...prev,
        {
          kind: "approval",
          id: last.plan_id,
          planId: last.plan_id,
          summaryText: last.summary_text,
          classification: last.classification,
          requiredTier: last.required_tier,
          permittedTier: last.permitted_tier,
          steps: last.plan_summary,
        },
      ]);
    }
  }, [envelopes]);

  // Auto-scroll to bottom on new bubble.
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [bubbles]);

  function submit() {
    const text = composer.trim();
    if (!text || status !== "open") return;
    setBubbles((prev) => [
      ...prev,
      { kind: "user", id: crypto.randomUUID(), text, imageRefs: [] },
    ]);
    send({ type: "user_message", text, mode });
    setComposer("");
    inputRef.current?.focus();
  }

  function runQuickChip(text: string) {
    setComposer(text);
    inputRef.current?.focus();
  }

  function approve(planId: string) {
    send({ type: "user_action", action_id: "approve_plan", params: { plan_id: planId } });
    setBubbles((prev) => prev.filter((b) => !(b.kind === "approval" && b.planId === planId)));
  }

  function decline(planId: string) {
    // POC: client-side dismiss only. Real impl will send a decline
    // user_action so Aide can free the pending plan slot.
    setBubbles((prev) => prev.filter((b) => !(b.kind === "approval" && b.planId === planId)));
  }

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-2 no-scrollbar">
        {bubbles.length === 0
          ? <EmptyState onRunChip={runQuickChip} />
          : bubbles.map(renderBubble(approve, decline))}
      </div>
      <Composer
        value={composer}
        onChange={setComposer}
        onSubmit={submit}
        inputRef={inputRef}
        disabled={status !== "open"}
        pendingAttachments={pendingAttachmentCount}
        placeholder={TRITON_PLACEHOLDER}
      />
    </div>
  );
}

function renderBubble(
  approve: (planId: string) => void,
  decline: (planId: string) => void,
) {
  return (bubble: Bubble) => {
    if (bubble.kind === "user") {
      return <UserBubbleView key={bubble.id} bubble={bubble} />;
    }
    if (bubble.kind === "assistant") {
      return <AssistantBubbleView key={bubble.id} bubble={bubble} />;
    }
    return (
      <ApprovalBubbleView
        key={bubble.id}
        bubble={bubble}
        onApprove={() => approve(bubble.planId)}
        onDecline={() => decline(bubble.planId)}
      />
    );
  };
}

function EmptyState({
  onRunChip,
}: { onRunChip: (text: string) => void }) {
  return (
    <div className="flex h-full flex-col items-start justify-end gap-3 pb-2 text-(--color-ink-muted)">
      <p className="text-sm">Good day. What would you like to look around, book, or have us negotiate?</p>
      <div className="flex flex-wrap gap-1.5">
        {TRITON_QUICK_CHIPS.map((chip) => (
          <button
            key={chip}
            onClick={() => onRunChip(chip)}
            className="rounded-(--radius-pill) bg-(--color-canvas-card) px-2.5 py-1 text-xs text-(--color-ink-muted) hover:text-(--color-ink) hover:bg-(--color-canvas-elev)"
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  );
}

function UserBubbleView({ bubble }: { bubble: UserBubble }) {
  return (
    <div className="mb-3 flex justify-end">
      <div className="max-w-[80%] rounded-2xl rounded-br-md bg-(--color-accent-gold) px-3 py-2 text-sm text-stone-900">
        {bubble.text}
      </div>
    </div>
  );
}

function AssistantBubbleView({ bubble }: { bubble: AssistantBubble }) {
  // If any step is a PACT scout.dispatch with a sealed deal, render an
  // inline sealed-deal card alongside the assistant text. Per BRD §05
  // the sealed deal IS the deliverable — surface it as a first-class
  // card, not a buried debug detail.
  const sealedSteps = bubble.steps.filter((s) =>
    s.tool === "scout.dispatch"
    && s.result
    && typeof s.result === "object"
    && (s.result as Record<string, unknown>).status === "sealed"
  );
  // kb.search results render as inline Alexandria-style storefront
  // cards so consumers see the recommendations (image, title, rating,
  // source) without leaving chat. The aide responder enforces a
  // relevance check before recommending, so anything reaching this
  // bubble is on-topic.
  const searchSteps = bubble.steps.filter((s) =>
    s.tool === "kb.search"
    && s.status === "ok"
    && s.result
    && typeof s.result === "object"
    && Array.isArray((s.result as { items?: unknown }).items)
    && ((s.result as { items: unknown[] }).items.length > 0)
  );
  return (
    <div className="mb-3 flex justify-start">
      <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-(--color-canvas-card) px-3 py-2.5 text-sm">
        <p>{bubble.text}</p>
        {searchSteps.map((s) => (
          <KbSearchCardsInline
            key={s.step_id}
            items={(s.result as { items: KbItem[] }).items}
          />
        ))}
        {sealedSteps.map((s) => (
          <SealedDealCard key={s.step_id} payload={s.result as Record<string, unknown>} />
        ))}
        {bubble.steps.length > 0 && (
          <details className="mt-2 text-xs text-(--color-ink-muted)">
            <summary className="cursor-pointer text-(--color-ink-faint) hover:text-(--color-ink-muted)">
              {bubble.steps.length} step{bubble.steps.length === 1 ? "" : "s"}
            </summary>
            <ul className="mt-1 space-y-0.5">
              {bubble.steps.map((s) => (
                <li key={s.step_id}>
                  <span className={s.status === "ok" ? "text-emerald-400" : "text-red-400"}>
                    {s.status === "ok" ? "✓" : "✗"}
                  </span>{" "}
                  <span className="font-mono">{s.tool}</span>
                  {s.error && <span className="text-red-300"> — {s.error}</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}


function SealedDealCard({ payload }: { payload: Record<string, unknown> }) {
  const winner = (payload.winner ?? {}) as Record<string, unknown>;
  const sealed = (payload.sealed_deal ?? {}) as Record<string, unknown>;
  const agreement = (sealed.agreement ?? {}) as Record<string, unknown>;
  const proposals = (payload.proposals as unknown[]) ?? [];
  const counterOffered = payload.counter != null;
  const negotiationMeta = (payload.metadata ?? {}) as Record<string, unknown>;
  const partnerScope = negotiationMeta.partner_fit_scope as string | undefined;
  const partnerCount = negotiationMeta.partner_fit_count as number | undefined;

  const provider = String(winner.provider_name ?? agreement.provider_name ?? "(provider)");
  const grossPrice = Number(winner.price_jpy ?? 0);
  const netPrice = Number(agreement.final_price_jpy ?? grossPrice);
  const pointsApplied = Number(agreement.points_applied ?? 0);
  const cancellation = String(agreement.cancellation ?? "free");
  const dealId = String(sealed.deal_id ?? "");
  const sourceUrl = String(winner.provider_source_url ?? "");

  return (
    <div className="mt-2 rounded-xl border border-(--color-accent-gold-dim)/40 bg-(--color-canvas-elev) p-3 text-xs">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wide text-(--color-accent-gold)">
          Sealed on PACT · Midnight ✓
        </span>
        <span className="text-[10px] text-(--color-ink-faint) font-mono">{dealId}</span>
      </div>
      {partnerScope && (
        <p className="mt-1 text-[10px] uppercase tracking-wide text-(--color-accent-gold-dim)">
          Partner-Fit · {partnerScope}
          {partnerCount ? ` of ${partnerCount}` : ""}
        </p>
      )}
      <p className="mt-1 text-sm font-medium text-(--color-ink) line-clamp-2">{provider}</p>
      <p className="mt-1 text-(--color-ink-muted)">
        ¥{netPrice.toLocaleString()} · {cancellation} cancellation
      </p>
      {pointsApplied > 0 && (
        <p className="text-(--color-ink-faint)">
          ¥{grossPrice.toLocaleString()} − {pointsApplied.toLocaleString()} PayPay points
        </p>
      )}
      <p className="mt-1 text-(--color-ink-faint)">
        {proposals.length} provider{proposals.length === 1 ? "" : "s"} bid
        {counterOffered && <> · counter offered</>}
      </p>
      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block rounded-(--radius-pill) bg-(--color-canvas-card) px-2.5 py-1 text-(--color-ink-muted) hover:text-(--color-ink)"
        >
          View on source ↗
        </a>
      )}
    </div>
  );
}

// kb.search projection — see hybrid_search_records in src/lifestyle_api/qdrant.py
interface KbItem {
  assetID: string;
  title: string;
  mediaType?: string;
  imageURL?: string;
  imageURLs?: string[];
  videoURLs?: string[];
  menuPdfURLs?: string[];
  sourceURL?: string;
  vertical?: string;
  geography?: { region?: string; city?: string; area?: string };
  externalRating?: number;
  externalRatingCount?: number;
  priceLowJpy?: number;
  priceHighJpy?: number;
  score?: number;
}


function KbSearchCardsInline({ items }: { items: KbItem[] }) {
  // Cap to the top 3 — assistant text already names them. More than
  // three becomes overwhelming inside chat.
  const top = items.slice(0, 3);
  return (
    <div className="mt-2 flex flex-col gap-2">
      {top.map((item) => (
        <KbCard key={item.assetID} item={item} />
      ))}
    </div>
  );
}


function KbCard({ item }: { item: KbItem }) {
  const image = item.imageURL || item.imageURLs?.[0];
  const hasVideo = (item.videoURLs?.length ?? 0) > 0;
  const hasMenu = (item.menuPdfURLs?.length ?? 0) > 0;
  const locale = [item.geography?.area, item.geography?.city, item.geography?.region]
    .filter(Boolean).join(" · ");
  return (
    <div className="overflow-hidden rounded-xl border border-(--color-canvas-elev) bg-(--color-canvas-elev)">
      {image && (
        <div className="aspect-[16/9] w-full bg-stone-900">
          <img
            src={image}
            alt={item.title}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        </div>
      )}
      <div className="p-2.5 text-xs">
        <p className="line-clamp-2 text-sm font-medium text-(--color-ink)">{item.title}</p>
        {locale && (
          <p className="mt-0.5 text-(--color-ink-faint)">{locale}</p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-(--color-ink-muted)">
          {typeof item.externalRating === "number" && (
            <span>★ {item.externalRating.toFixed(1)}{item.externalRatingCount ? ` (${item.externalRatingCount})` : ""}</span>
          )}
          {item.priceLowJpy && item.priceHighJpy && (
            <span>· ¥{item.priceLowJpy.toLocaleString()}–¥{item.priceHighJpy.toLocaleString()}</span>
          )}
          {hasVideo && <span className="rounded bg-(--color-canvas-card) px-1.5 py-0.5 text-[10px]">▶ video</span>}
          {hasMenu && (
            <a
              href={item.menuPdfURLs![0]}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded bg-(--color-canvas-card) px-1.5 py-0.5 text-[10px] hover:text-(--color-ink)"
            >
              menu pdf ↗
            </a>
          )}
        </div>
        {item.sourceURL && (
          <a
            href={item.sourceURL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block rounded-(--radius-pill) bg-(--color-canvas-card) px-2.5 py-1 text-(--color-ink-muted) hover:text-(--color-ink)"
          >
            View on source ↗
          </a>
        )}
      </div>
    </div>
  );
}


function ApprovalBubbleView({
  bubble,
  onApprove,
  onDecline,
}: {
  bubble: ApprovalBubble;
  onApprove: () => void;
  onDecline: () => void;
}) {
  return (
    <div className="mb-3 flex justify-start">
      <div className="max-w-[88%] rounded-2xl rounded-bl-md border border-(--color-accent-gold-dim)/50 bg-(--color-canvas-card) px-3 py-2.5 text-sm">
        <p className="text-(--color-accent-gold)">Reviewing before I act —</p>
        <p className="mt-1 text-(--color-ink-muted) text-xs">
          This plan needs <span className="font-mono">{bubble.requiredTier}</span> approval
          (you allow up to <span className="font-mono">{bubble.permittedTier}</span>).
        </p>
        {bubble.steps.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-(--color-ink-muted)">
            {bubble.steps.map((s, i) => (
              <li key={`${bubble.planId}-${i}`}>
                <span className="font-mono">{s.tool}</span> — {s.rationale}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex gap-2">
          <button
            onClick={onApprove}
            className="rounded-(--radius-pill) bg-(--color-accent-gold) px-3 py-1 text-xs font-medium text-stone-900"
          >
            Approve
          </button>
          <button
            onClick={onDecline}
            className="rounded-(--radius-pill) bg-transparent px-3 py-1 text-xs text-(--color-ink-muted) hover:text-(--color-ink)"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

function Composer({
  value,
  onChange,
  onSubmit,
  inputRef,
  disabled,
  pendingAttachments,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  disabled: boolean;
  pendingAttachments: number;
  placeholder?: string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex items-end gap-2 border-t border-white/5 bg-(--color-canvas-elev) px-3 py-2"
    >
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        rows={1}
        placeholder={placeholder ?? "Say what you have in mind"}
        disabled={disabled}
        aria-label="Message"
        className="flex-1 resize-none rounded-2xl bg-(--color-canvas-card) px-3 py-2 text-sm placeholder:text-(--color-ink-faint) focus:outline-none focus:ring-1 focus:ring-(--color-accent-gold)/40 disabled:opacity-50"
      />
      <button
        type="submit"
        disabled={disabled || value.trim().length === 0}
        aria-label="Send"
        className="rounded-full bg-(--color-accent-gold) p-2 text-stone-900 disabled:opacity-40"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
        </svg>
        {pendingAttachments > 0 && (
          <span className="absolute -mt-1 ml-2 rounded-full bg-(--color-accent-coral) px-1.5 text-[10px] text-stone-900">
            {pendingAttachments}
          </span>
        )}
      </button>
    </form>
  );
}
